import React, { useEffect, useState, useCallback } from 'react';

const S = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: '#ffffff',
    border: '1px solid #dbe4ef',
    borderRadius: 12,
    width: 640,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 48px)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '18px 24px',
    borderBottom: '1px solid #dbe4ef',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  icone: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0,
  },
  tituloWrap: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  titulo: {
    fontSize: 16,
    fontWeight: 700,
    color: '#0f172a',
  },
  subtitulo: {
    fontSize: 12,
    color: '#64748b',
  },
  btnFechar: {
    background: 'transparent',
    border: 'none',
    fontSize: 22,
    lineHeight: 1,
    color: '#64748b',
    cursor: 'pointer',
    padding: '4px 8px',
    flexShrink: 0,
  },
  corpo: {
    padding: '12px 16px 16px',
    overflowY: 'auto',
    flex: 1,
  },
  estadoInfo: {
    fontSize: 13,
    color: '#64748b',
    background: '#f8fafc',
    border: '1px dashed #cbd5e1',
    borderRadius: 8,
    padding: '14px 16px',
    textAlign: 'center',
  },
  estadoErro: {
    fontSize: 13,
    color: '#b91c1c',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '12px 14px',
  },
  commit: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '12px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    marginBottom: 8,
    background: '#fbfdff',
  },
  commitTopo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 10,
    flexWrap: 'wrap',
  },
  commitAssunto: {
    fontSize: 13,
    fontWeight: 600,
    color: '#0f172a',
    wordBreak: 'break-word',
    flex: 1,
    minWidth: 0,
  },
  commitHash: {
    fontSize: 11,
    fontFamily: "'Consolas', 'Cascadia Code', monospace",
    color: '#2563eb',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 5,
    padding: '2px 7px',
    flexShrink: 0,
  },
  commitMeta: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    fontSize: 12,
    color: '#64748b',
  },
  rodape: {
    padding: '12px 24px',
    borderTop: '1px solid #dbe4ef',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    flexShrink: 0,
  },
  btnRecarregar: {
    padding: '8px 16px',
    background: '#eff6ff',
    color: '#2563eb',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

function formatarDataHora(valor) {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleString('pt-BR');
}

export default function CommitsViewer({ api, projectId, projectName, limit = 20, onClose }) {
  const [commits, setCommits] = useState([]);
  const [branch, setBranch] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const data = await api.getProjectCommits(projectId, limit);
      setCommits(data?.commits || []);
      setBranch(data?.branch || '');
    } catch (e) {
      setErro(e.message || 'Erro ao carregar commits');
    } finally {
      setCarregando(false);
    }
  }, [api, projectId, limit]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal}>
        <div style={S.header}>
          <div style={S.icone}>📜</div>
          <div style={S.tituloWrap}>
            <span style={S.titulo}>Últimos commits</span>
            <span style={S.subtitulo}>
              {projectName}{branch ? ` · branch ${branch}` : ''}
            </span>
          </div>
          <button style={S.btnFechar} onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div style={S.corpo}>
          {carregando ? (
            <div style={S.estadoInfo}>Carregando commits...</div>
          ) : erro ? (
            <div style={S.estadoErro}>⚠ {erro}</div>
          ) : commits.length === 0 ? (
            <div style={S.estadoInfo}>Nenhum commit encontrado.</div>
          ) : (
            commits.map((c) => (
              <div key={c.hash || c.shortHash} style={S.commit}>
                <div style={S.commitTopo}>
                  <span style={S.commitAssunto}>{c.subject || '(sem mensagem)'}</span>
                  {c.shortHash && <span style={S.commitHash}>{c.shortHash}</span>}
                </div>
                <div style={S.commitMeta}>
                  <span>👤 {c.authorName || '-'}</span>
                  <span>🕒 {formatarDataHora(c.timestamp)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={S.rodape}>
          <button style={S.btnRecarregar} onClick={carregar} disabled={carregando}>
            {carregando ? 'Atualizando...' : 'Recarregar'}
          </button>
        </div>
      </div>
    </div>
  );
}
